import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getMyBenefits, submitBarnHire, GetMyBenefitsOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Gift, QrCode, ShoppingBag, TrendingDown, Calendar, Home, Loader2, ExternalLink } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };
const fmtGBP = (n: number) => `\u00A3${n.toFixed(2)}`;

type Benefits = GetMyBenefitsOutputType;

const STATUS_COLORS: Record<string, string> = {
  Pending: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  Approved: 'bg-green-500/10 text-green-700 border-green-500/20',
  Declined: 'bg-red-500/10 text-red-700 border-red-500/20',
};

export default function Benefits() {
  const { user } = useAuth();
  const [data, setData] = useState<Benefits | null>(null);
  const [loading, setLoading] = useState(true);
  const [barnOpen, setBarnOpen] = useState(false);
  const [barnForm, setBarnForm] = useState({ eventName: '', eventDate: '', eventDescription: '', expectedGuests: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    getMyBenefits({}).then(setData).catch(() => toast.error('Failed to load benefits')).finally(() => setLoading(false));
  }, [user]);

  const handleBarnSubmit = async () => {
    if (!barnForm.eventName || !barnForm.eventDate) { toast.error('Event name and date are required'); return; }
    setSubmitting(true);
    try {
      await submitBarnHire({ eventName: barnForm.eventName, eventDate: barnForm.eventDate, eventDescription: barnForm.eventDescription, expectedGuests: parseInt(barnForm.expectedGuests) || 0 });
      toast.success('Barn hire request submitted! Your manager will be in touch.');
      setBarnOpen(false);
      setBarnForm({ eventName: '', eventDate: '', eventDescription: '', expectedGuests: '' });
      const refreshed = await getMyBenefits({});
      setData(refreshed);
    } catch (e: any) { toast.error(e.message || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="px-6 py-8 max-w-3xl mx-auto space-y-4">
      <Skeleton className="h-8 w-48" />
      {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-6 py-6">
        <h1 className="text-xl font-bold text-foreground flex items-center gap-2"><Gift className="w-5 h-5 text-primary" /> Staff Benefits</h1>
        <p className="text-sm text-muted-foreground mt-1">Your discount card, purchase history, and perks</p>
      </div>

      <div className="px-6 py-6 max-w-3xl mx-auto space-y-5">
        {/* Discount Card */}
        <div className="border border-primary/30 bg-primary/5 rounded-xl p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <QrCode className="w-5 h-5 text-primary" />
                <h2 className="font-semibold text-foreground">Your Staff Discount Card</h2>
              </div>
              <p className="text-2xl font-bold text-primary mt-2">{data?.discountLevel}</p>
              <p className="text-xs text-muted-foreground mt-1">{user?.firstName} {user?.lastName} · {user?.employeeNumber ? `Emp #${user.employeeNumber}` : user?.email}</p>
            </div>
            <div className="bg-card rounded-xl p-3 border border-border">
              <QrCode className="w-12 h-12 text-muted-foreground/50" />
              <p className="text-[10px] text-center text-muted-foreground mt-1">Show at bar</p>
            </div>
          </div>
          <div className="mt-4 text-xs text-muted-foreground bg-background/60 rounded-lg px-3 py-2">
            Present this screen or your staff ID at the bar to claim your discount. Valid for personal use only — not transferable.
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: ShoppingBag, label: 'Purchases', value: data?.stats.totalTransactions || 0 },
            { icon: TrendingDown, label: 'Total Spent', value: fmtGBP(data?.stats.totalSpent || 0) },
            { icon: Gift, label: 'Total Saved', value: fmtGBP(data?.stats.totalSaved || 0) },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="border border-border rounded-xl p-4 bg-card text-center">
              <Icon className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="text-xl font-bold text-foreground">{value}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>

        {/* Purchase History */}
        <div className="border border-border rounded-xl bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Purchase History</h2>
            <span className="text-xs text-muted-foreground">{data?.purchases.length || 0} transactions</span>
          </div>
          {(data?.purchases.length ?? 0) === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No purchases recorded yet</p>
              <p className="text-xs mt-1">Your manager will upload purchase records</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {data?.purchases.slice(0, 20).map(p => (
                <div key={p.id} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{p.itemDescription}</p>
                    <p className="text-xs text-muted-foreground">{fmtDate(p.purchaseDate)} · {p.category}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-foreground">{fmtGBP(p.amountSpent)}</p>
                    {p.discountSaved > 0 && <p className="text-xs text-green-600">saved {fmtGBP(p.discountSaved)}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Barn Hire */}
        <div className="border border-border rounded-xl bg-card p-5">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1"><Home className="w-5 h-5 text-primary" /><h2 className="font-semibold text-foreground">Free Barn Hire</h2></div>
              <p className="text-sm text-muted-foreground">Eligible staff can request free use of the barn for a personal family event once per year, subject to availability and manager approval.</p>
            </div>
          </div>
          {data?.barnHireRequests.map(r => (
            <div key={r.id} className="border border-border rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">{r.eventName}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(r.eventDate)} · Submitted {fmtDate(r.submittedAt)}</p>
                  {r.managerNotes && <p className="text-xs text-muted-foreground mt-1 italic">Manager: "{r.managerNotes}"</p>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_COLORS[r.status] || 'bg-muted text-muted-foreground border-border'}`}>{r.status}</span>
              </div>
            </div>
          ))}
          {data?.canRequestBarnHire ? (
            <Button onClick={() => setBarnOpen(true)} variant="outline" className="gap-2">
              <Calendar className="w-4 h-4" /> Request Barn Hire
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2">You have already used your free barn hire allocation this year. This benefit resets each calendar year.</p>
          )}
        </div>
      </div>

      <Dialog open={barnOpen} onOpenChange={v => !submitting && setBarnOpen(v)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Home className="w-4 h-4 text-primary" /> Request Free Barn Hire</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Event Name *</label><Input value={barnForm.eventName} onChange={e => setBarnForm(f => ({ ...f, eventName: e.target.value }))} placeholder="e.g. Family Birthday Party" /></div>
            <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Preferred Date *</label><Input type="date" value={barnForm.eventDate} onChange={e => setBarnForm(f => ({ ...f, eventDate: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Event Description</label><Textarea value={barnForm.eventDescription} onChange={e => setBarnForm(f => ({ ...f, eventDescription: e.target.value }))} placeholder="Brief description of the event..." className="min-h-20" /></div>
            <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Expected Number of Guests</label><Input type="number" value={barnForm.expectedGuests} onChange={e => setBarnForm(f => ({ ...f, expectedGuests: e.target.value }))} placeholder="e.g. 30" /></div>
            <p className="text-xs text-muted-foreground">This request is subject to manager approval and barn availability. You'll be notified once reviewed.</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setBarnOpen(false)} disabled={submitting}>Cancel</Button>
              <Button className="flex-1" onClick={handleBarnSubmit} disabled={submitting}>
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : 'Submit Request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
